import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { EditDialog } from "@/components/EditDialog";
import { ArrowLeft, Download, Trash2, FileImage, ImageIcon, Wand2, Eye, Copy, Check, Edit, ChevronLeft, ChevronRight, RotateCcw } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useStreamingVisualCreator } from "@/hooks/useStreamingVisualCreator";
import { toast } from "sonner";
import { Navigation } from "@/components/Navigation";

interface ContentItem {
  id: string;
  kind: 'visual' | 'humanized';
  series_id: string;
  version: number;
  title: string | null;
  prompt: string | null;
  data: any;
  created_at: string;
  updated_at: string;
}

interface Project {
  series_id: string;
  title: string;
  prompt: string | null;
  visual: ContentItem | null;
  humanized: ContentItem | null;
  created_at: string;
  version: number;
  totalVersions: number;
}

const MyContent = () => {
  const { user } = useAuth();
  const { streamingState, streamGenerateVisual, reset: resetStreaming } = useStreamingVisualCreator();
  const [visualContent, setVisualContent] = useState<ContentItem[]>([]);
  const [humanizedContent, setHumanizedContent] = useState<ContentItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [copiedItems, setCopiedItems] = useState<Set<string>>(new Set());
  const [editingItem, setEditingItem] = useState<ContentItem | null>(null);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [selectedVersions, setSelectedVersions] = useState<{[seriesId: string]: number}>({});

  const visualTypes = [
    { value: "auto", label: "Auto (AI Decides)" },
    { value: "mindmap", label: "Mind Map" },
    { value: "flowchart", label: "Flowchart" },
    { value: "table", label: "Table/Chart" },
    { value: "timeline", label: "Timeline" },
    { value: "org-chart", label: "Organization Chart" },
    { value: "process-diagram", label: "Process Diagram" },
    { value: "network-diagram", label: "Network Diagram" },
    { value: "pie-chart", label: "Pie Chart" },
    { value: "bar-chart", label: "Bar Chart" },
    { value: "line-graph", label: "Line Graph" },
    { value: "scatter-plot", label: "Scatter Plot" },
    { value: "venn-diagram", label: "Venn Diagram" },
    { value: "gantt-chart", label: "Gantt Chart" },
    { value: "checklist", label: "Checklist" },
    { value: "calendar", label: "Calendar" },
    { value: "kanban", label: "Kanban Board" },
    { value: "wireframe", label: "Wireframe" },
    { value: "mockup", label: "UI Mockup" },
    { value: "sitemap", label: "Site Map" },
  ];

  useEffect(() => {
    if (user) {
      fetchContent();
    }
  }, [user]);

  const fetchContent = async () => {
    try {
      console.log('Fetching content for user:', user?.id);
      console.log('User object:', user);
      
      if (!user?.id) {
        console.log('No user ID available');
        return;
      }

      const { data, error } = await supabase
        .from('content_items')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });

      console.log('Supabase query result:', { data, error });
      console.log('Data length:', data?.length);

      if (error) {
        console.error('Supabase error:', error);
        throw error;
      }

      if (!data) {
        console.log('No data returned from query');
        setVisualContent([]);
        setHumanizedContent([]);
        return;
      }

      const visuals = data.filter(item => item.kind === 'visual');
      const humanized = data.filter(item => item.kind === 'humanized');

      console.log('Filtered results:', { 
        visuals: visuals.length, 
        humanized: humanized.length,
        visualItems: visuals,
        humanizedItems: humanized
      });

      setVisualContent(visuals);
      setHumanizedContent(humanized);

      // Set default selected versions (latest version for each series)
      const defaultVersions: {[seriesId: string]: number} = {};
      [...visuals, ...humanized].forEach(item => {
        if (!defaultVersions[item.series_id] || item.version > defaultVersions[item.series_id]) {
          defaultVersions[item.series_id] = item.version;
        }
      });
      console.log('Default versions:', defaultVersions);
      setSelectedVersions(defaultVersions);
    } catch (error) {
      console.error('Error fetching content:', error);
      toast.error('Failed to load content');
    } finally {
      setLoading(false);
    }
  };

  const deleteItem = async (id: string) => {
    if (!confirm('Are you sure you want to delete this item?')) return;

    console.log('Deleting item with ID:', id);

    try {
      const { error } = await supabase
        .from('content_items')
        .delete()
        .eq('id', id);

      if (error) {
        console.error('Supabase delete error:', error);
        throw error;
      }

      // Refresh content after deletion
      await fetchContent();
      toast.success('Item deleted successfully');
    } catch (error) {
      console.error('Error deleting item:', error);
      toast.error('Failed to delete item');
    }
  };

  const deleteProject = async (seriesId: string) => {
    if (!confirm('Are you sure you want to delete this project?')) return;
    try {
      console.log('Deleting entire project series:', seriesId);
      const { error } = await supabase
        .from('content_items')
        .delete()
        .eq('series_id', seriesId);
      if (error) throw error;
      await fetchContent();
      toast.success('Project deleted successfully');
    } catch (error) {
      console.error('Error deleting project:', error);
      toast.error('Failed to delete project');
    }
  };

  const copyToClipboard = async (text: string, itemId: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedItems(prev => new Set(prev).add(itemId));
      toast.success("Copied to clipboard!");
      setTimeout(() => {
        setCopiedItems(prev => {
          const newSet = new Set(prev);
          newSet.delete(itemId);
          return newSet;
        });
      }, 2000);
    } catch (err) {
      toast.error("Failed to copy to clipboard");
    }
  };

  const sanitizeSvg = (input: string) => {
    let svg = input.trim();
    svg = svg.replace(/<script[\s\S]*?<\/script>/gi, '');
    svg = svg.replace(/<a[\s\S]*?<\/a>/gi, (match) => {
      const content = match.replace(/<\/?a[^>]*>/gi, '');
      return content;
    });
    svg = svg.replace(/\s+href\s*=\s*["'][^"']*["']/gi, '');
    svg = svg.replace(/\s+xlink:href\s*=\s*["'][^"']*["']/gi, '');
    svg = svg.replace(/\s+on\w+\s*=\s*["'][^"']*["']/gi, '');
    svg = svg.replace(/<svg(\s[^>]*)?>/i, (m) => {
      let tag = m;
      if (!/xmlns=/.test(tag)) tag = tag.replace('<svg', '<svg xmlns="http://www.w3.org/2000/svg"');
      if (!/xmlns:xlink=/.test(tag)) tag = tag.replace('<svg', '<svg xmlns:xlink="http://www.w3.org/1999/xlink"');
      return tag;
    });

    // Make responsive and handle overflow
    svg = svg.replace(/<svg([^>]*)>/i, (m, attrs) => {
      let a = attrs || '';
      a = a.replace(/\swidth="[^"]*"/i, '');
      a = a.replace(/\sheight="[^"]*"/i, '');
      const hasPAR = /preserveAspectRatio=/i.test(a);
      const par = hasPAR ? '' : ' preserveAspectRatio="xMidYMid meet"';
      const styleAttr = /style=/i.test(a) ? '' : ' style="max-width:100%;height:auto;overflow:visible;"';
      return `<svg${a} width="100%" height="auto"${par}${styleAttr}>`;
    });

    // Fix text positioning and overflow issues
    svg = svg.replace(/<text([^>]*)>/gi, (m, attrs) => {
      if (!/text-anchor/.test(attrs)) {
        attrs += ' text-anchor="start"';
      }
      if (!/dominant-baseline/.test(attrs)) {
        attrs += ' dominant-baseline="auto"';
      }
      return `<text${attrs} style="overflow:visible;word-wrap:break-word;">`;
    });

    // Ensure proper container sizing for text elements
    svg = svg.replace(/<foreignObject([^>]*)>/gi, (m, attrs) => {
      if (!/overflow/.test(attrs)) {
        return `<foreignObject${attrs} style="overflow:visible;">`;
      }
      return m;
    });

    svg = svg.replace(/&(?!amp;|lt;|gt;|quot;|apos;|#\d+;|#x[0-9A-Fa-f]+;)/g, '&amp;');
    return svg;
  };

  const downloadSvg = (svgContent: string, title: string) => {
    try {
      let cleanSvg = sanitizeSvg(svgContent.trim());
      if (!cleanSvg.startsWith('<?xml')) {
        cleanSvg = '<?xml version="1.0" encoding="UTF-8"?>\n' + cleanSvg;
      }
      
      const blob = new Blob([cleanSvg], { type: 'image/svg+xml;charset=utf-8' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${title || 'visual'}.svg`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.success("SVG downloaded!");
    } catch (error) {
      toast.error("Failed to download SVG");
    }
  };

  const downloadPng = async (svgContent: string, title: string) => {
    try {
      // Import html2canvas dynamically
      const html2canvas = (await import('html2canvas')).default;
      
      const sanitized = sanitizeSvg(svgContent);
      
      // Replace common Font Awesome private-use glyphs with emoji fallbacks
      let processed = sanitized;
      const faMap: Record<string, string> = {
        'F2B5': '🤝', // handshake
        'F0C0': '👥',
        'F0E0': '📧',
        'F002': '🔍',
        'F073': '📅',
      };
      Object.entries(faMap).forEach(([u, emoji]) => {
        const patterns = [
          new RegExp(`&#x${u.toLowerCase()};`, 'gi'),
          new RegExp(`\\${u}`, 'gi'),
          new RegExp(`${String.fromCharCode(parseInt(u, 16))}`, 'g'),
        ];
        patterns.forEach((p) => { processed = processed.replace(p, emoji); });
      });
      
      // Create a temporary div to render the SVG with proper FontAwesome support
      const tempDiv = document.createElement('div');
      tempDiv.style.position = 'absolute';
      tempDiv.style.left = '-9999px';
      tempDiv.style.top = '-9999px';
      tempDiv.style.width = '1200px';
      tempDiv.style.height = 'auto';
      tempDiv.style.backgroundColor = '#ffffff';
      tempDiv.style.padding = '20px';
      tempDiv.innerHTML = processed;

      // Inline local Font Awesome fonts to avoid CDN/CORS issues
      const style = document.createElement('style');
      style.textContent = `
        @font-face { font-family: 'Font Awesome 6 Free'; src: url('/fonts/fa-solid-900.woff2') format('woff2'); font-weight: 900; font-style: normal; font-display: swap; }
        @font-face { font-family: 'Font Awesome 6 Brands'; src: url('/fonts/fa-brands-400.woff2') format('woff2'); font-weight: 400; font-style: normal; font-display: swap; }
        svg { max-width: 100% !important; height: auto !important; }
        text[class*="fa"], tspan[class*="fa"], .fa, .fas, .far, .fab, .fal, .fad { font-family: 'Font Awesome 6 Free','Font Awesome 6 Brands',sans-serif !important; font-weight: 900 !important; }
      `;
      tempDiv.prepend(style);
      
      // Ensure FontAwesome is loaded (CDN fallback)
      const faHref = 'https://cdn.jsdelivr.net/npm/@fortawesome/fontawesome-free@6.5.2/css/all.min.css';
      let faLink = document.querySelector(`link[href="${faHref}"]`) as HTMLLinkElement | null;
      if (!faLink) {
        faLink = document.createElement('link');
        faLink.rel = 'stylesheet';
        faLink.href = faHref;
        faLink.crossOrigin = 'anonymous';
        document.head.appendChild(faLink);
      }
      
      document.body.appendChild(tempDiv);
      
      // Mark private-use glyphs to force FA font
      const markFa = () => {
        const nodes = tempDiv.querySelectorAll('text, tspan');
        nodes.forEach((el) => {
          const txt = (el.textContent || '').trim();
          if (/[\uE000-\uF8FF]/.test(txt)) {
            const prev = el.getAttribute('style') || '';
            el.setAttribute('style', `${prev};font-family:'Font Awesome 6 Free','Font Awesome 6 Brands',sans-serif;font-weight:900`);
          }
        });
      };
      markFa();
      
      // Wait for fonts to be ready
      try {
        await (document as any).fonts?.ready;
        await Promise.allSettled([
          (document as any).fonts?.load?.("900 16px 'Font Awesome 6 Free'"),
          (document as any).fonts?.load?.("400 16px 'Font Awesome 6 Free'"),
          (document as any).fonts?.load?.("400 16px 'Font Awesome 6 Brands'")
        ]);
      } catch {}
      // Give layout a tick
      await new Promise((r) => requestAnimationFrame(() => requestAnimationFrame(r)));
      
      try {
        const canvas = await html2canvas(tempDiv, {
          scale: 4,
          backgroundColor: '#ffffff',
          useCORS: true,
          allowTaint: false,
          onclone: (doc) => {
            // Ensure FA is present in the cloned document too
            if (!doc.querySelector(`link[href="${faHref}"]`)) {
              const l = doc.createElement('link');
              l.rel = 'stylesheet';
              l.href = faHref;
              (l as any).crossOrigin = 'anonymous';
              doc.head.appendChild(l);
            }
          }
        });
        
        canvas.toBlob((blob) => {
          if (blob) {
            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `${title || 'visual'}@4x.png`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(url);
            toast.success('High-quality PNG downloaded with icons!');
          } else {
            throw new Error('Failed to create PNG blob');
          }
        }, 'image/png');
        
      } finally {
        document.body.removeChild(tempDiv);
      }
    } catch (error) {
      console.error('PNG download error:', error);
      toast.error(`Failed to download PNG: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  };

  const downloadText = (text: string, title: string) => {
    const blob = new Blob([text], { type: 'text/plain' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${title || 'humanized-text'}.txt`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
    toast.success("Text downloaded!");
  };

  const openEditDialog = (item: ContentItem) => {
    setEditingItem(item);
    setIsEditDialogOpen(true);
  };

  const handleEdit = async (prompt: string, visualTypes?: string[], intensity?: string, options?: { mode: "describe" | "convert"; originalSvg?: string; }) => {
    if (!editingItem) return;

    try {
      let result;
      if (editingItem.kind === 'visual') {
        // Pass the original SVG content and editing parameters
        result = await streamGenerateVisual(
          prompt, 
          visualTypes?.[0] || 'auto',
          '4:3', // Default aspect ratio
          {
            mode: options?.mode || 'describe',
            originalSvg: editingItem.data.svg, // Pass original SVG for content preservation
            targetTypes: options?.mode === 'convert' ? visualTypes : null // Pass target types for conversion mode
          }
        );
        
        if (result?.svg) {
          // Save as new version
          const maxVersion = Math.max(...getVersionsForSeries(editingItem.series_id, 'visual').map(v => v.version));
          
          const { error } = await supabase
            .from('content_items')
            .insert({
              user_id: user?.id,
              kind: 'visual',
              series_id: editingItem.series_id,
              version: maxVersion + 1,
              title: editingItem.title,
              prompt: prompt,
              data: {
                svg: result.svg,
                dataUrl: result.dataUrl
              }
            });

          if (error) throw error;

          // Update local state and switch to new version
          await fetchContent();
          setSelectedVersions(prev => ({
            ...prev,
            [editingItem.series_id]: maxVersion + 1
          }));
          
          toast.success("New version created successfully!");
        }
      } else if (editingItem.kind === 'humanized') {
        // For humanized text, we'd need to implement text editing
        // This would require adding the useStreamingHumanizer hook here
        toast.info("Text editing will be available in the next update!");
        return;
      }
      
      setIsEditDialogOpen(false);
    } catch (error) {
      console.error('Error editing content:', error);
      toast.error('Failed to create new version');
    }
  };

  const getVersionsForSeries = (seriesId: string, kind: 'visual' | 'humanized') => {
    const items = kind === 'visual' ? visualContent : humanizedContent;
    return items.filter(item => item.series_id === seriesId).sort((a, b) => a.version - b.version);
  };

  const getDisplayedItem = (seriesId: string, kind: 'visual' | 'humanized'): ContentItem | null => {
    const selectedVersion = selectedVersions[seriesId];
    const items = kind === 'visual' ? visualContent : humanizedContent;
    return items.find(item => item.series_id === seriesId && item.version === selectedVersion) || null;
  };

  const switchVersion = (seriesId: string, direction: 'prev' | 'next') => {
    const allVersions = [...visualContent, ...humanizedContent]
      .filter(item => item.series_id === seriesId)
      .map(item => item.version)
      .filter((version, index, arr) => arr.indexOf(version) === index) // unique versions
      .sort((a, b) => a - b);
    
    const currentVersion = selectedVersions[seriesId];
    const currentIndex = allVersions.indexOf(currentVersion);
    
    let newIndex;
    if (direction === 'prev') {
      newIndex = currentIndex > 0 ? currentIndex - 1 : allVersions.length - 1;
    } else {
      newIndex = currentIndex < allVersions.length - 1 ? currentIndex + 1 : 0;
    }
    
    setSelectedVersions(prev => ({
      ...prev,
      [seriesId]: allVersions[newIndex]
    }));
  };

  // Create projects by grouping content by series_id
  const createProjects = (): Project[] => {
    const allContent = [...visualContent, ...humanizedContent];
    const seriesMap = new Map<string, { visual: ContentItem[], humanized: ContentItem[] }>();
    
    // Group all content by series_id
    allContent.forEach(item => {
      if (!seriesMap.has(item.series_id)) {
        seriesMap.set(item.series_id, { visual: [], humanized: [] });
      }
      seriesMap.get(item.series_id)![item.kind].push(item);
    });
    
    // Create projects from grouped content
    return Array.from(seriesMap.entries()).map(([seriesId, { visual, humanized }]) => {
      const selectedVersion = selectedVersions[seriesId] || 1;
      const visualItem = visual.find(v => v.version === selectedVersion);
      const humanizedItem = humanized.find(h => h.version === selectedVersion);
      
      // Get the most recent item to determine project details
      const allVersions = [...visual, ...humanized].sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
      const latest = allVersions[0];
      
      // Calculate total versions across both visual and text
      const totalVersions = Math.max(
        visual.length > 0 ? Math.max(...visual.map(v => v.version)) : 0,
        humanized.length > 0 ? Math.max(...humanized.map(h => h.version)) : 0
      );
      
      return {
        series_id: seriesId,
        title: latest?.title || 'Untitled Project',
        prompt: latest?.prompt || null,
        visual: visualItem || null,
        humanized: humanizedItem || null,
        created_at: latest?.created_at || new Date().toISOString(),
        version: selectedVersion,
        totalVersions
      };
    }).sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
  };

  const projects = createProjects();

  if (!user) {
    return (
      <div className="min-h-screen bg-bg flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold mb-4">Please sign in to view your content</h1>
          <Link to="/auth">
            <Button>Sign In</Button>
          </Link>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-bg flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin w-8 h-8 border-2 border-primary border-t-transparent rounded-full mx-auto mb-4"></div>
          <p>Loading your content...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-bg">
      {/* Header */}
      <header className="bg-card border-b border-border shadow-card">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 sm:gap-0">
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
              <Link 
                to="/" 
                className="inline-flex items-center gap-2 text-muted-foreground hover:text-primary"
              >
                <ArrowLeft className="w-4 h-4" />
                Back to Home
              </Link>
              <div className="hidden sm:block h-6 w-px bg-border" />
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-primary rounded-full flex items-center justify-center">
                  <Download className="w-5 h-5 text-primary-foreground" />
                </div>
                <div>
                  <h1 className="text-xl sm:text-2xl font-bold text-primary">
                    My Content
                  </h1>
                  <p className="text-sm text-muted-foreground">
                    View and manage your saved content
                  </p>
                </div>
              </div>
            </div>
            <div className="w-full sm:w-auto">
              <Navigation />
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-6">
          <h2 className="text-xl font-semibold mb-2">My Projects ({projects.length})</h2>
          <p className="text-muted-foreground">Complete projects with text and visual content</p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
          {projects.map((project) => {
            // Handle complete projects (new format)
            const completeProject = [...visualContent, ...humanizedContent].find(
              item => item.series_id === project.series_id && 
                     item.data.originalText && 
                     item.data.humanizedText
            );

            if (completeProject) {
              const { originalText, humanizedText, extractedVisuals = [] } = completeProject.data;
              
              return (
                <Card key={project.series_id} className="relative group">
                  <CardHeader className="pb-3">
                    <CardTitle className="text-base sm:text-lg flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2">
                      <span className="line-clamp-2">{project.title}</span>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        {project.totalVersions > 1 && (
                          <div className="flex items-center gap-1">
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => switchVersion(project.series_id, 'prev')}
                              className="w-6 h-6 p-0"
                            >
                              <ChevronLeft className="w-3 h-3" />
                            </Button>
                            <span className="text-xs bg-muted px-2 py-1 rounded min-w-[40px] text-center">
                              v{project.version}
                            </span>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => switchVersion(project.series_id, 'next')}
                              className="w-6 h-6 p-0"
                            >
                              <ChevronRight className="w-3 h-3" />
                            </Button>
                          </div>
                        )}
                        {project.totalVersions === 1 && (
                          <span className="text-xs bg-muted px-2 py-1 rounded">
                            v{project.version}
                          </span>
                        )}
                      </div>
                    </CardTitle>
                    <p className="text-sm text-muted-foreground line-clamp-2">
                      {originalText.substring(0, 100)}...
                    </p>
                  </CardHeader>
                  <CardContent>
                    {/* Original Text */}
                    <div className="mb-4">
                      <h4 className="text-sm font-medium text-muted-foreground mb-2">Original Text:</h4>
                      <div className="bg-muted/5 rounded-lg p-3 border max-h-20 overflow-y-auto">
                        <p className="text-xs text-muted-foreground line-clamp-3">
                          {originalText}
                        </p>
                      </div>
                    </div>

                    {/* Humanized Text */}
                    <div className="mb-4">
                      <h4 className="text-sm font-medium mb-2 flex items-center gap-2">
                        <Wand2 className="w-4 h-4" />
                        Humanized Content
                      </h4>
                      <div className="bg-background rounded-lg p-3 border max-h-40 overflow-y-auto">
                        <div className="prose prose-sm max-w-none">
                          {(() => {
                            // Check if content contains SVG
                            if (humanizedText.includes('<svg')) {
                              const parts = humanizedText.split(/(<svg[\s\S]*?<\/svg>)/g);
                              return parts.map((part, index) => {
                                if (part.trim().startsWith('<svg')) {
                                  return (
                                    <div key={index} className="my-2 flex justify-center">
                                       <div 
                                         className="max-w-full overflow-auto bg-white rounded border p-1 scale-75"
                                         dangerouslySetInnerHTML={{ __html: sanitizeSvg(part) }}
                                       />
                                    </div>
                                  );
                                } else {
                                  return part.trim() ? (
                                    <p key={index} className="text-xs whitespace-pre-wrap">
                                      {part}
                                    </p>
                                  ) : null;
                                }
                              });
                            } else {
                              return (
                                <p className="text-xs whitespace-pre-wrap line-clamp-4">
                                  {humanizedText}
                                </p>
                              );
                            }
                          })()}
                        </div>
                      </div>
                    </div>

                    {/* Extracted Visuals */}
                    {extractedVisuals.length > 0 && (
                      <div className="mb-4">
                        <h4 className="text-sm font-medium mb-2 flex items-center gap-2">
                          <ImageIcon className="w-4 h-4" />
                          Visuals ({extractedVisuals.length})
                        </h4>
                        <div className="grid grid-cols-2 gap-2">
                          {extractedVisuals.slice(0, 4).map((visual: any, index: number) => (
                            <div key={index} className="bg-muted/10 rounded p-2 border">
                              <div 
                                className="w-full h-16 [&>svg]:w-full [&>svg]:h-full [&>svg]:object-contain"
                                dangerouslySetInnerHTML={{ __html: sanitizeSvg(visual.svg) }}
                              />
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                      <div className="flex flex-col sm:flex-row gap-1 mt-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => copyToClipboard(humanizedText, completeProject.id)}
                          className="flex-1 text-xs justify-center"
                        >
                          {copiedItems.has(completeProject.id) ? (
                            <>
                              <Check className="w-3 h-3 sm:mr-1" />
                              <span className="hidden sm:inline">Copied</span>
                            </>
                          ) : (
                            <>
                              <Copy className="w-3 h-3 sm:mr-1" />
                              <span className="hidden sm:inline">Copy</span>
                            </>
                          )}
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => downloadText(humanizedText, project.title)}
                          className="flex-1 text-xs justify-center"
                        >
                          <Download className="w-3 h-3 sm:mr-1" />
                          <span className="hidden sm:inline">Download</span>
                        </Button>
                      </div>

                    {/* Project Edit Button */}
                    <div className="flex gap-2 mb-2 pt-2 border-t">
                      <Button
                        size="sm"
                        variant="default"
                        onClick={() => {
                          // Store payload in sessionStorage to avoid URL size limits
                          try {
                            const payload = {
                              originalText,
                              humanizedText,
                              extractedVisuals,
                            };
                            sessionStorage.setItem('editProjectPayload', JSON.stringify(payload));
                          } catch {}
                          // Navigate with lightweight flag
                          window.location.href = '/?mode=edit&src=my-content';
                        }}
                        className="w-full"
                      >
                        <Edit className="w-4 h-4 mr-2" />
                        Edit Full Project
                      </Button>
                    </div>

                    {/* Delete Project */}
                    <div className="flex gap-2 pt-2 border-t">
                      <Button
                        size="sm"
                        variant="destructive"
                        onClick={() => deleteProject(project.series_id)}
                        className="w-full"
                      >
                        <Trash2 className="w-4 h-4 mr-2" />
                        Delete Project
                      </Button>
                    </div>

                    <div className="mt-2 text-xs text-muted-foreground">
                      Created: {new Date(project.created_at).toLocaleDateString()}
                      {project.totalVersions > 1 && (
                        <span className="ml-2">• {project.totalVersions} versions</span>
                      )}
                    </div>
                  </CardContent>
                </Card>
              );
            }

            // Handle legacy separate visual/text items
            return (
              <Card key={project.series_id} className="relative group">
                <CardHeader className="pb-3">
                  <CardTitle className="text-lg flex items-center justify-between">
                    {project.title}
                    <div className="flex items-center gap-2">
                      {project.totalVersions > 1 && (
                        <div className="flex items-center gap-1">
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => switchVersion(project.series_id, 'prev')}
                            className="w-6 h-6 p-0"
                          >
                            <ChevronLeft className="w-3 h-3" />
                          </Button>
                          <span className="text-xs bg-muted px-2 py-1 rounded min-w-[40px] text-center">
                            v{project.version}
                          </span>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => switchVersion(project.series_id, 'next')}
                            className="w-6 h-6 p-0"
                          >
                            <ChevronRight className="w-3 h-3" />
                          </Button>
                        </div>
                      )}
                      {project.totalVersions === 1 && (
                        <span className="text-xs bg-muted px-2 py-1 rounded">
                          v{project.version}
                        </span>
                      )}
                    </div>
                  </CardTitle>
                  {project.prompt && (
                    <p className="text-sm text-muted-foreground line-clamp-2">
                      {project.prompt}
                    </p>
                  )}
                </CardHeader>
                <CardContent>
                  {/* Legacy Visual Content */}
                  {project.visual && (
                    <div className="mb-4">
                      <h4 className="text-sm font-medium mb-2 flex items-center gap-2">
                        <ImageIcon className="w-4 h-4" />
                        Visual
                      </h4>
                      <div className="relative bg-muted/10 rounded-lg p-4">
                        <div 
                          className="w-full h-48 [&>svg]:w-full [&>svg]:h-full [&>svg]:object-contain"
                          dangerouslySetInnerHTML={{ __html: sanitizeSvg(project.visual.data.svg) }}
                        />
                      </div>
                      <div className="flex gap-1 mt-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => copyToClipboard(project.visual!.data.svg, project.visual!.id)}
                          className="flex-1 text-xs"
                        >
                          {copiedItems.has(project.visual.id) ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => downloadSvg(project.visual!.data.svg, project.title)}
                          className="flex-1 text-xs"
                        >
                          <FileImage className="w-3 h-3" />
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => downloadPng(project.visual!.data.svg, project.title)}
                          className="flex-1 text-xs"
                        >
                          <ImageIcon className="w-3 h-3" />
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => openEditDialog(project.visual!)}
                          className="flex-1 text-xs"
                        >
                          <Edit className="w-3 h-3" />
                        </Button>
                      </div>
                    </div>
                  )}

                  {/* Legacy Humanized Text */}
                  {project.humanized && (
                    <div className="mb-4">
                      <h4 className="text-sm font-medium mb-2 flex items-center gap-2">
                        <Wand2 className="w-4 h-4" />
                        Humanized Text
                      </h4>
                      <div className="bg-background rounded-lg p-3 border max-h-40 overflow-y-auto">
                        <div className="prose prose-sm max-w-none">
                          {(() => {
                            const content = project.humanized.data.text || '';
                            
                            // Check if content contains SVG
                            if (content.includes('<svg')) {
                              const parts = content.split(/(<svg[\s\S]*?<\/svg>)/g);
                              return parts.map((part, index) => {
                                if (part.trim().startsWith('<svg')) {
                                  return (
                                    <div key={index} className="my-2 flex justify-center">
                                       <div 
                                         className="max-w-full overflow-auto bg-white rounded border p-1 scale-75"
                                         dangerouslySetInnerHTML={{ __html: sanitizeSvg(part) }}
                                       />
                                    </div>
                                  );
                                } else {
                                  return part.trim() ? (
                                    <p key={index} className="text-xs whitespace-pre-wrap">
                                      {part}
                                    </p>
                                  ) : null;
                                }
                              });
                            } else {
                              return (
                                <p className="text-xs whitespace-pre-wrap line-clamp-4">
                                  {content}
                                </p>
                              );
                            }
                          })()}
                        </div>
                      </div>
                      <div className="flex gap-1 mt-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => copyToClipboard(project.humanized!.data.text, project.humanized!.id)}
                          className="flex-1 text-xs"
                        >
                          {copiedItems.has(project.humanized.id) ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => downloadText(project.humanized!.data.text, project.title)}
                          className="flex-1 text-xs"
                        >
                          <Download className="w-3 h-3" />
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => openEditDialog(project.humanized!)}
                          className="flex-1 text-xs"
                        >
                          <Edit className="w-3 h-3" />
                        </Button>
                      </div>
                    </div>
                  )}

                  {/* Project Edit Button for Legacy */}
                  <div className="flex gap-2 mb-2 pt-2 border-t">
                    <Button
                      size="sm"
                      variant="default"
                      onClick={() => {
                        // For legacy projects, combine available data
                        const originalText = project.humanized?.data.text || project.visual?.prompt || '';
                        const humanizedText = project.humanized?.data.text || '';
                        const extractedVisuals = project.visual ? [{ svg: project.visual.data.svg }] : [];
                        
                        try {
                          const payload = { originalText, humanizedText, extractedVisuals };
                          sessionStorage.setItem('editProjectPayload', JSON.stringify(payload));
                        } catch {}
                        window.location.href = '/?mode=edit&src=my-content';
                      }}
                      className="w-full"
                    >
                      <Edit className="w-4 h-4 mr-2" />
                      Edit Full Project
                    </Button>
                  </div>

                   {/* Delete Project */}
                   <div className="flex gap-2 pt-2 border-t">
                     <Button
                       size="sm"
                       variant="destructive"
                       onClick={() => deleteProject(project.series_id)}
                       className="w-full"
                     >
                       <Trash2 className="w-4 h-4 mr-2" />
                       Delete Project
                     </Button>
                   </div>

                  <div className="mt-2 text-xs text-muted-foreground">
                    Created: {new Date(project.created_at).toLocaleDateString()}
                    {project.totalVersions > 1 && (
                      <span className="ml-2">• {project.totalVersions} versions</span>
                    )}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {projects.length === 0 && (
          <div className="text-center py-12">
            <Wand2 className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
            <h3 className="text-lg font-semibold mb-2">No projects yet</h3>
            <p className="text-muted-foreground mb-4">
              Create your first project by humanizing text or creating visuals
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/">
                <Button className="w-full sm:w-auto">
                  <Wand2 className="w-4 h-4 mr-2" />
                  Humanize Text
                </Button>
              </Link>
              <Link to="/visual-creator">
                <Button variant="outline" className="w-full sm:w-auto">
                  <ImageIcon className="w-4 h-4 mr-2" />
                  Create Visual
                </Button>
              </Link>
            </div>
          </div>
        )}
      </main>

      <EditDialog
        open={isEditDialogOpen}
        onOpenChange={setIsEditDialogOpen}
        type={editingItem?.kind === 'visual' ? 'visual' : 'text'}
        currentContent={editingItem?.kind === 'visual' ? editingItem.data.svg : editingItem?.data.text || ''}
        onEdit={handleEdit}
        isGenerating={streamingState.isGenerating}
        previewContent={streamingState.svg || streamingState.content}
        title={`Edit ${editingItem?.kind === 'visual' ? 'Visual' : 'Text'}`}
      />
    </div>
  );
};

export default MyContent;